// Bonds (v2 addition, Ben 13 Oct 2026): Singapore Government Securities (SGS) bonds, Singapore T-bills,
// Singapore Savings Bonds (SSB), US Treasuries and corporate bonds, as their own asset class ('bond').
//
// Records: trades are ordinary trades with type 'bond'. The quantity is the face amount (e.g. 10000) and
// the price is per 100 of face (e.g. 101.20), so tq() is the face and tp() divides the price by 100: all
// the usual maths (cost, average cost, gains, sales) then works per 1 of face. Prices in S.prices are per
// 1 of face too, like every other price (the Bonds tab shows them × 100).
// Each bond's terms live in S.assets['bond:<code>'].bond: { kind, coupon, freq, maturity, issue, first,
// rates, name }. The code is the SGS/T-bill/SSB issue code, the US CUSIP, or the SGX code of a corporate bond.
//
// Values: SGS, US Treasuries and corporate bonds at their market price (from the price service); T-bills
// rise in a straight line from what you paid to face value at maturity; Savings Bonds at face value (they
// can be redeemed at face value in any month).
// Coupons are recorded automatically on each payment date (income kind 'coupon', id cpn-<code>-<date>),
// for the face held the day before. On the maturity date the bond is recorded as sold at face value
// (trade id mat-<code>). Deleting either keeps it deleted (S.dismissed).
/* eslint-disable @typescript-eslint/no-explicit-any */
import { addDaysIso, days, isoOf, now, today } from './clock';
import { N, keyOf, meta, okC, type Ccy, type Price, type State } from './state';

export type BondKind = 'sgs' | 'tbill' | 'ssb' | 'ust' | 'corp';
export const BOND_KINDS: [BondKind, string][] = [['sgs', 'SGS bond'], ['tbill', 'T-bill'], ['ssb', 'Savings Bond'], ['ust', 'US Treasury'], ['corp', 'Corporate bond']];
export const BOND_LABEL: Record<string, string> = Object.fromEntries(BOND_KINDS);
export interface BondTerms {
  kind: BondKind; coupon: number; freq: number; maturity: string; issue?: string; first?: string;
  /** Savings Bonds: the coupon for each year, 1 to 10 (% a year). */
  rates?: number[]; name?: string;
  /** Coupons are recorded automatically from this date only (set when a bond held as a stock is moved). */
  since?: string;
}

export const bondKey = (code: string) => keyOf('bond', code);
export function termsOf(S: State, code: string): BondTerms | null {
  const b = meta(S, bondKey(code)).bond;
  if (!b || !BOND_LABEL[b.kind] || !/^\d{4}-\d{2}-\d{2}$/.test(b.maturity || '')) return null;
  return { kind: b.kind, coupon: N(b.coupon), freq: b.kind === 'tbill' ? 0 : N(b.freq) || 2, maturity: b.maturity, issue: b.issue || undefined, first: b.first || undefined, rates: Array.isArray(b.rates) ? b.rates.map(N) : undefined, name: b.name, since: b.since || undefined };
}
export function setTerms(S: State, code: string, t: Partial<BondTerms>, ccy?: Ccy) {
  const k = bondKey(code), m = Object.assign({}, meta(S, k));
  m.bond = Object.assign({}, m.bond || {}, t);
  if (ccy && okC(ccy)) m.ccy = ccy;
  if (t.name && !m.nameSet) m.name = t.name;
  S.assets[k] = m;
}

// ---- dates ----
const ymdParts = (d: string) => d.split('-').map(Number);
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
/** d moved by n months, keeping month-end dates at month end (US Treasuries mature on the 30th/31st). */
export function addMonths(d: string, n: number): string {
  const [y, m, dd] = ymdParts(d), eom = dd === lastDay(y, m), t = new Date(Date.UTC(y, m - 1 + n, 1));
  const ny = t.getUTCFullYear(), nm = t.getUTCMonth() + 1, day = eom ? lastDay(ny, nm) : Math.min(dd, lastDay(ny, nm));
  return `${ny}-${String(nm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/** Every coupon date, oldest first: stepping back from maturity, after the issue date. */
export function couponDates(t: BondTerms): string[] {
  if (t.kind === 'tbill' || !(t.freq > 0)) return [];
  const step = 12 / t.freq, out: string[] = [];
  // Savings Bonds too: every 6 months back from maturity. (MAS's "first_int_date" isn't reliably the first
  // coupon: for GX23100T, issued Oct 2023 and paying since Apr 2024, it says Apr 2027.)
  const start = t.issue || addMonths(t.maturity, -12 * 50);
  for (let k = 0; k < 400; k++) { const d = addMonths(t.maturity, -step * k); if (d <= start) break; out.push(d); }
  return out.reverse();
}
/** Coupon per 1 of face paid on date d. Savings Bonds: that year's rate. */
export function couponPer1(t: BondTerms, d: string): number {
  if (t.kind === 'tbill' || !(t.freq > 0)) return 0;
  if (t.kind === 'ssb' && t.rates && t.rates.length && t.issue) {
    const yrs = Math.floor((days(t.issue, d) - 1) / 365.25), r = t.rates[Math.min(Math.max(yrs, 0), t.rates.length - 1)];
    return N(r) / 100 / t.freq;
  }
  return t.coupon / 100 / t.freq;
}

// ---- holdings ----
const bondTxns = (S: State, code: string) => S.txns.filter((x) => x.type === 'bond' && x.sym === code);
/** Face held at the end of the day before d (buys and sales dated before d). */
export function faceBefore(S: State, code: string, d: string): number {
  let q = 0; for (const x of bondTxns(S, code)) if (x.date < d) q += x.side === 'buy' ? N(x.qty) : -N(x.qty);
  return Math.max(0, +q.toFixed(6));
}
const bondCodes = (S: State) => [...new Set(S.txns.filter((x) => x.type === 'bond').map((x) => x.sym))];
const ccyOf = (S: State, code: string): Ccy => { const m = meta(S, bondKey(code)); if (okC(m.ccy)) return m.ccy; const t = bondTxns(S, code)[0]; return t && okC(t.ccy) ? t.ccy : 'SGD'; };

/** Records coupons paid up to today and maturities reached. Returns how many records were added. */
export function settleBonds(S: State): { coupons: number; matured: number } {
  const td = today(), r = { coupons: 0, matured: 0 };
  for (const code of bondCodes(S)) {
    const t = termsOf(S, code); if (!t) continue;
    const ccy = ccyOf(S, code), firstBuy = bondTxns(S, code).filter((x) => x.side === 'buy').map((x) => x.date).sort()[0];
    if (!firstBuy) continue;
    for (const d of couponDates(t)) {
      if (d > td || d <= firstBuy || (t.since && d < t.since)) continue;
      const id = 'cpn-' + code + '-' + d; if (S.dismissed.includes(id) || S.income.some((i) => i.id === id)) continue;
      // A coupon you recorded yourself (or one moved over with a bond held as a stock) within 10 days counts.
      if (S.income.some((i) => i.type === 'bond' && i.sym === code && i.kind !== 'staking' && Math.abs(days(i.date, d)) <= 10)) continue;
      const face = faceBefore(S, code, d), c = couponPer1(t, d); if (!(face > 0) || !(c > 0)) continue;
      S.income.push({ id, type: 'bond', kind: 'coupon', sym: code, date: d, gross: +(face * c).toFixed(2), tax: 0, ccy,
        note: `Coupon ${+(c * t.freq * 100).toFixed(4)}% a year on ${face.toLocaleString('en-US')} face` });
      r.coupons++;
    }
    const mid = 'mat-' + code;
    if (t.maturity <= td && !S.dismissed.includes(mid) && !S.txns.some((x) => x.id === mid)) {
      const face = faceBefore(S, code, addDaysIso(t.maturity, 1));
      if (face > 0) { S.txns.push({ id: mid, type: 'bond', sym: code, date: t.maturity, side: 'sell', qty: face, price: 100, fee: 0, ccy, note: 'Matured: repaid at face value' }); r.matured++; }
    }
  }
  return r;
}

/** The price per 1 of face for a T-bill (straight line from your average purchase price to face value at
 *  maturity) or a Savings Bond (face value); null for bonds valued at their market price. */
export function heldPrice(S: State, code: string, at = now()): Price | null {
  const t = termsOf(S, code); if (!t || (t.kind !== 'tbill' && t.kind !== 'ssb')) return null;
  const ccy = ccyOf(S, code), d = isoOf(at);
  if (t.kind === 'ssb') return { price: 1, ccy, at, src: 'face', prev: 1, pd: d };
  let q = 0, c = 0, w = 0;
  for (const x of bondTxns(S, code)) if (x.side === 'buy' && x.date <= d) { const f = N(x.qty); q += f; c += f * N(x.price) / 100 + N(x.fee); w += f * Date.parse(x.date + 'T00:00:00Z'); }
  if (!(q > 0)) return null;
  const p0 = c / q, t0 = w / q, t1 = Date.parse(t.maturity + 'T00:00:00Z');
  const at0 = Date.parse(d + 'T00:00:00Z'), f = (x: number) => (t1 > t0 ? Math.min(1, Math.max(0, (x - t0) / (t1 - t0))) : 1);
  return { price: p0 + (1 - p0) * f(at0), ccy, at, src: 'accrued', prev: p0 + (1 - p0) * f(at0 - 864e5), pd: d };
}

// ---- pricing from a yield curve, and yield to maturity ----
export type Curve = [number, number][];   // [years, yield %], by years
/** The yield (%) for a time to maturity, by straight lines between curve points, flat beyond the ends. */
export function curveYield(curve: Curve, yrs: number): number | null {
  const c = curve.filter(([x, y]) => x > 0 && isFinite(y)).sort((a, b) => a[0] - b[0]); if (!c.length) return null;
  if (yrs <= c[0][0]) return c[0][1];
  for (let i = 1; i < c.length; i++) if (yrs <= c[i][0]) { const [x0, y0] = c[i - 1], [x1, y1] = c[i]; return y0 + (y1 - y0) * (yrs - x0) / (x1 - x0); }
  return c[c.length - 1][1];
}
/** Clean price per 100 for a yield (%), on date d: coupons and face discounted at the yield, compounded at
 *  the coupon frequency, less accrued interest. */
export function priceFromYield(t: BondTerms, y: number, d: string): number {
  const yrs = (x: string) => days(d, x) / 365.25;
  if (t.kind === 'tbill' || !(t.freq > 0)) return 100 / Math.pow(1 + y / 100, yrs(t.maturity));
  // Coupon bonds: discounted per coupon period (the market convention), w = the part of the current
  // period still to run; clean price = that less accrued interest.
  const f = t.freq, cpn = t.coupon / f, rate = y / 100 / f;
  const all = couponDates(t), dates = all.filter((x) => x > d), next = dates[0] || t.maturity;
  const prev = [...all].reverse().find((x) => x <= d) || addMonths(next, -12 / f);
  const w = Math.max(0, days(d, next)) / Math.max(1, days(prev, next));
  let pv = 0; dates.forEach((_, k) => { pv += cpn / Math.pow(1 + rate, k + w); });
  pv += 100 / Math.pow(1 + rate, Math.max(0, dates.length - 1) + w);
  return pv - cpn * (1 - w);
}
/** Yield to maturity (%) for a clean price per 100 on date d, or null. */
export function yieldToMaturity(t: BondTerms, price100: number, d: string): number | null {
  if (!(price100 > 0) || t.maturity <= d) return null;
  let lo = -5, hi = 50;
  for (let i = 0; i < 100; i++) { const mid = (lo + hi) / 2; if (priceFromYield(t, mid, d) > price100) lo = mid; else hi = mid; }
  const y = (lo + hi) / 2; return y > -4.9 && y < 49.9 ? y : null;
}

// ---- SGX names of corporate bonds, e.g. "FPTrea 4.49%270916XB#" → 4.49% maturing 2027-09-16 ----
export function termsFromSgxName(name: string): Partial<BondTerms> | null {
  const m = String(name || '').match(/(\d+(?:\.\d+)?)%\s*(?:cb)?(\d{2})(\d{2})(\d{2})/i); if (!m) return null;
  const maturity = `20${m[2]}-${m[3]}-${m[4]}`; if (isNaN(Date.parse(maturity))) return null;
  return { kind: 'corp', coupon: +m[1], freq: 2, maturity };
}

/** Moves a bond recorded as a stock (e.g. an SGX retail bond like EHZB) to the Bonds class: its trades,
 *  its payments (dividends become coupons), its settings and price. Trade prices become per 100 of face:
 *  SGX quotes these per S$1 of face (1.012 → 101.2); a price already per 100 is kept; a price per S$1,000
 *  bond (1012) with a count of bonds becomes face × 1,000 at 101.2. Coupons before the move stay as they were
 *  recorded; later ones are recorded automatically. Returns the trades moved. */
export function moveStockToBond(S: State, sym: string, t: BondTerms, ccy?: Ccy): number {
  let n = 0;
  for (const x of S.txns) {
    if (x.type !== 'stock' || x.sym !== sym) continue;
    const p = N(x.price);
    if (p >= 500) { x.qty = +(N(x.qty) * 1000).toFixed(6); x.price = +(p / 10).toFixed(6); }
    else if (p < 20) x.price = +(p * 100).toFixed(6);
    x.type = 'bond'; n++;
  }
  for (const i of S.income) if (i.type === 'stock' && i.sym === sym) { i.type = 'bond'; if (i.kind === 'dividend') i.kind = 'coupon'; }
  S.upcoming = S.upcoming.filter((e) => e.sym !== sym);
  const ok = keyOf('stock', sym), nk = bondKey(sym);
  if (S.assets[ok]) { const m = { ...S.assets[ok] }; delete m.provider; delete m.quote; S.assets[nk] = m; delete S.assets[ok]; }
  if (S.prices[ok]) { const p = { ...S.prices[ok] }; if (p.price > 20) { p.price /= 100; if (p.prev) p.prev /= 100; } S.prices[nk] = p; delete S.prices[ok]; }
  setTerms(S, sym, { ...t, since: today() }, ccy);   // earlier coupons stay as you recorded them
  return n;
}
